/**
 * Utilitários de formatação para valores numéricos e monetários
 * Seguindo o padrão brasileiro: ponto para milhar, vírgula para decimal
 */

/**
 * Formata um número para o padrão brasileiro
 * @param value - Valor numérico a ser formatado
 * @param decimals - Número de casas decimais (padrão: 2)
 * @returns String formatada no padrão brasileiro
 */
export const formatBrazilianNumber = (value: number, decimals: number = 2): string => {
  if (isNaN(value)) return '0,00';
  
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formata um valor monetário para o padrão brasileiro
 * @param value - Valor monetário a ser formatado
 * @returns String formatada como moeda brasileira (R$ X.XXX,XX)
 */
export const formatBrazilianCurrency = (value: number): string => {
  if (isNaN(value)) return 'R$ 0,00';
  
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

/**
 * Formata um valor numérico inteiro para o padrão brasileiro
 * @param value - Valor numérico a ser formatado
 * @returns String formatada sem decimais
 */
export const formatBrazilianInteger = (value: number): string => {
  if (isNaN(value)) return '0';
  
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

/**
 * Formata um percentual para o padrão brasileiro
 * @param value - Valor percentual a ser formatado
 * @param decimals - Número de casas decimais (padrão: 1)
 * @returns String formatada com símbolo de percentual
 */
export const formatBrazilianPercentage = (value: number, decimals: number = 1): string => {
  if (isNaN(value)) return '0,0%';
  
  const formattedValue = value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return `${formattedValue}%`;
};

/**
 * Formata um valor monetário para exibição (sem símbolo R$)
 * @param value - Valor monetário a ser formatado
 * @returns String formatada no padrão brasileiro (X.XXX,XX)
 */
export const formatBrazilianCurrencyValue = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) return '0,00';
  
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Formata um valor monetário para input (aceita apenas números e formata enquanto digita)
 * @param value - String do input
 * @returns String formatada no padrão brasileiro (X.XXX,XX)
 */
export const formatCurrencyInput = (value: string): string => {
  // Remove tudo que não é número
  const numbersOnly = value.replace(/\D/g, '');
  
  if (!numbersOnly) return '';
  
  // Converte para número e divide por 100 para ter centavos
  const numberValue = parseFloat(numbersOnly) / 100;
  
  // Formata no padrão brasileiro
  return numberValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Converte um valor formatado brasileiro de volta para número
 * @param formattedValue - String formatada (ex: "1.234,56")
 * @returns Número (ex: 1234.56)
 */
export const parseBrazilianCurrency = (formattedValue: string): number => {
  if (!formattedValue) return 0;
  
  // Remove pontos (separadores de milhar) e substitui vírgula por ponto
  const cleaned = formattedValue.replace(/\./g, '').replace(',', '.');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formata um número de telefone para o padrão brasileiro
 * @param phone - Número de telefone a ser formatado
 * @returns String formatada (00) 00000-0000 ou (00) 0000-0000
 */
export const formatPhone = (phone: string | undefined): string => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

/**
 * Formata um CPF para o padrão brasileiro
 * @param cpf - CPF a ser formatado (apenas dígitos ou com formatação)
 * @returns String formatada 000.000.000-00
 */
export const formatCPF = (cpf: string | undefined | null): string => {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '').slice(0, 11);
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return cpf;
};
